import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSCGRuleSourceInfo, getRuleUsage } from '../services/api';
import TrustScoreBadge from './TrustScoreBadge';
import ViewInPdfButton from './ViewInPdfButton';

const classificationColors = {
  'TOP SECRET': { bg: 'rgba(234,179,8,0.18)', text: '#eab308' },
  'SECRET': { bg: 'rgba(239,68,68,0.18)', text: '#ef4444' },
  'CONFIDENTIAL': { bg: 'rgba(59,130,246,0.18)', text: '#3b82f6' },
  'UNCLASSIFIED': { bg: 'rgba(34,197,94,0.18)', text: '#22c55e' },
};

export default function RuleHoverCard({ ruleId }) {
  const { data: sourceInfo, isLoading } = useQuery({
    queryKey: ['scg-source', ruleId],
    queryFn: () => getSCGRuleSourceInfo(ruleId),
    enabled: !!ruleId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const { data: usage } = useQuery({
    queryKey: ['rule-usage', ruleId],
    queryFn: () => getRuleUsage(ruleId),
    enabled: !!ruleId,
    staleTime: 60 * 1000,
    retry: false,
  });

  if (isLoading) {
    return (
      <div style={{ minWidth: '260px', color: '#cbd5e1', padding: '6px 4px' }}>
        Loading {ruleId}…
      </div>
    );
  }
  if (!sourceInfo) {
    return (
      <div style={{ minWidth: '220px', color: '#fca5a5' }}>
        Rule {ruleId} not found
      </div>
    );
  }

  const rule = sourceInfo.rule_details || sourceInfo.rule_data || {};
  const item = (rule.item || rule.description || rule.enhanced_description || '').slice(0, 200);
  const classLevel = rule.classification_level || 'UNKNOWN';
  const colors = classificationColors[classLevel] || { bg: 'rgba(107,114,128,0.18)', text: '#9ca3af' };
  const page = sourceInfo.location_metadata?.page_number || sourceInfo.citation_display?.page_reference;
  const friendly = sourceInfo.citation_display?.friendly_reference;

  return (
    <div style={{ minWidth: '280px', maxWidth: '340px', textAlign: 'left' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#e2e8f0' }}>{ruleId}</span>
        <TrustScoreBadge
          trustScore={usage?.trust_score}
          citedCount={usage?.cited_count || 0}
          overrideCount={usage?.override_count || 0}
          size="sm"
        />
      </div>
      {classLevel !== 'UNKNOWN' && (
        <div style={{
          display: 'inline-block', padding: '2px 6px', borderRadius: '4px',
          fontSize: '10px', fontWeight: 700, fontFamily: 'monospace',
          background: colors.bg, color: colors.text, marginBottom: '6px',
        }}>
          {classLevel}
        </div>
      )}
      {item && (
        <div style={{ fontSize: '12px', color: '#e2e8f0', marginBottom: '6px', lineHeight: 1.4 }}>
          {item}
        </div>
      )}
      {(friendly || page) && (
        <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '6px' }}>
          {friendly || `Page ${page}`}
        </div>
      )}
      <ViewInPdfButton
        ruleId={ruleId}
        sourceInfo={sourceInfo}
        variant="text-link"
        size="sm"
      />
    </div>
  );
}
